import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { usePageTitle } from '../hooks/usePageTitle';
import { API_URL } from '../lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import {
  ArrowLeft,
  Search,
  Loader2,
  Users,
  Building,
  Merge,
  ShieldAlert,
  Hand,
  X,
  ThumbsDown,
  EyeOff,
  Trash2,
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const SimilarityBadge = ({ value, manual }) => {
  if (manual) {
    return (
      <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-purple-100 text-purple-700">
        Manual selection
      </span>
    );
  }
  const pct = Math.round(value * 100);
  const color =
    pct >= 95 ? 'bg-red-100 text-red-700' : pct >= 90 ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700';
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${color}`}>
      {pct}% similar
    </span>
  );
};

const DuplicateGroupCard = ({ group, kind, onMerged, onIgnored }) => {
  const [winnerId, setWinnerId] = useState(group.records[0].id);
  const [showConfirm, setShowConfirm] = useState(false);
  const [merging, setMerging] = useState(false);
  const [ignoring, setIgnoring] = useState(false);

  const winner = group.records.find((r) => r.id === winnerId);
  const losers = group.records.filter((r) => r.id !== winnerId);

  const performMerge = async () => {
    setMerging(true);
    try {
      const endpoint =
        kind === 'companies' ? '/merge/companies' : '/merge/persons';
      const res = await axios.post(`${API_URL}${endpoint}`, {
        winner_id: winnerId,
        loser_ids: losers.map((r) => r.id),
      });
      const s = res.data.summary || {};
      const parts = [];
      if (s.persons_updated) parts.push(`${s.persons_updated} persons`);
      if (s.classes_updated) parts.push(`${s.classes_updated} classes`);
      if (s.certificates_updated) parts.push(`${s.certificates_updated} certificates`);
      if (s.users_updated) parts.push(`${s.users_updated} users`);
      const detail = parts.length ? ` (updated ${parts.join(', ')})` : '';
      toast.success(`Merged ${losers.length} record${losers.length === 1 ? '' : 's'}${detail}`);
      const filled = Array.isArray(s.fields_filled) ? s.fields_filled : [];
      if (filled.length) {
        const pretty = filled.map((f) => f.replace(/_/g, ' ')).join(', ');
        toast.success(`Imported into winner: ${pretty}`, { duration: 6000 });
      }
      setShowConfirm(false);
      onMerged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Merge failed');
    } finally {
      setMerging(false);
    }
  };

  const performIgnore = async () => {
    setIgnoring(true);
    try {
      const apiKind = kind === 'companies' ? 'companies' : 'persons';
      await axios.post(`${API_URL}/merge/ignore`, {
        kind: apiKind,
        record_ids: group.records.map((r) => r.id),
      });
      toast.success("Marked as 'not duplicates' — won't be suggested again");
      onIgnored?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to ignore pair');
    } finally {
      setIgnoring(false);
    }
  };

  return (
    <Card className="border-slate-200">
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <div className="flex items-center gap-3">
          <SimilarityBadge value={group.similarity} manual={group.manual} />
          <span className="text-sm text-slate-500">{group.records.length} records</span>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label className="text-xs uppercase tracking-wide text-slate-500 mb-2 block">
            Pick the record to KEEP. Other records will be merged into it and soft-deleted.
          </Label>
          <RadioGroup value={winnerId} onValueChange={setWinnerId} className="space-y-2">
            {group.records.map((r) => (
              <label
                key={r.id}
                htmlFor={`winner-${r.id}`}
                className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition ${
                  r.id === winnerId
                    ? 'border-emerald-400 bg-emerald-50'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <RadioGroupItem value={r.id} id={`winner-${r.id}`} className="mt-1" />
                <div className="flex-1">
                  {kind === 'companies' ? (
                    <>
                      <div className="font-medium text-slate-900">{r.name}</div>
                      <div className="text-xs text-slate-500 mt-0.5 space-x-3">
                        {r.email && <span>📧 {r.email}</span>}
                        {r.telephone && <span>📞 {r.telephone}</span>}
                        {r.city && <span>🏙 {r.city}</span>}
                        {r.country && <span>{r.country}</span>}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="font-medium text-slate-900">
                        {r.first_name} {r.last_name}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5 space-x-3">
                        {r.id_number && <span>ID: {r.id_number}</span>}
                        {r.email && <span>📧 {r.email}</span>}
                        {r.mobile && <span>📱 {r.mobile}</span>}
                      </div>
                      {r.course_company_names && r.course_company_names.length > 0 && (
                        <div className="text-xs text-slate-600 mt-1">
                          🏢 Trained for:{' '}
                          <span className="font-medium text-slate-700">
                            {r.course_company_names.join(', ')}
                          </span>
                        </div>
                      )}
                    </>
                  )}
                  <div className="text-[11px] text-slate-400 mt-1">
                    Created {r.created_at ? new Date(r.created_at).toLocaleDateString() : '-'}
                  </div>
                </div>
                {r.id === winnerId && (
                  <span className="text-xs font-medium text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded">
                    KEEP
                  </span>
                )}
              </label>
            ))}
          </RadioGroup>
        </div>
        <div className="flex justify-end gap-2">
          {!group.manual && (
            <Button
              variant="outline"
              size="sm"
              disabled={ignoring || merging}
              onClick={performIgnore}
              data-testid="ignore-pair-btn"
              title="Mark these as NOT duplicates so they aren't suggested again"
            >
              {ignoring ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <ThumbsDown className="w-4 h-4 mr-2" />
              )}
              Not a duplicate
            </Button>
          )}
          <Button
            variant="destructive"
            size="sm"
            disabled={!winner || losers.length === 0}
            onClick={() => setShowConfirm(true)}
            data-testid="merge-action-btn"
          >
            <Merge className="w-4 h-4 mr-2" />
            Merge {losers.length} into {kind === 'companies' ? winner?.name : `${winner?.first_name} ${winner?.last_name}`}
          </Button>
        </div>
      </CardContent>

      <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm merge</AlertDialogTitle>
            <AlertDialogDescription>
              <div className="space-y-2 text-sm">
                <div>
                  <strong className="text-emerald-700">Keeping:</strong>{' '}
                  {kind === 'companies'
                    ? winner?.name
                    : `${winner?.first_name} ${winner?.last_name}`}
                </div>
                <div>
                  <strong className="text-red-700">Merging into it (will be soft-deleted):</strong>
                  <ul className="list-disc ml-5 mt-1">
                    {losers.map((l) => (
                      <li key={l.id}>
                        {kind === 'companies' ? l.name : `${l.first_name} ${l.last_name}`}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="text-xs text-slate-500 mt-2">
                  All references (classes, certificates, employees) will be re-linked to the kept record.
                  Soft-deleted records remain in the database for audit but won't appear in lists.
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={merging}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={performMerge}
              disabled={merging}
              className="bg-red-600 hover:bg-red-700"
            >
              {merging ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Merge className="w-4 h-4 mr-2" />}
              {merging ? 'Merging...' : 'Yes, merge now'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

const ManualSearchSection = ({ kind, onPickGroup }) => {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [picked, setPicked] = useState({}); // id -> record

  // Debounced search
  useEffect(() => {
    const trimmed = search.trim();
    if (trimmed.length < 2) {
      setResults([]);
      return;
    }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const endpoint = kind === 'companies' ? '/companies/lookup' : '/persons/lookup';
        const res = await axios.get(`${API_URL}${endpoint}?search=${encodeURIComponent(trimmed)}&limit=20`);
        setResults(res.data.items || []);
      } catch (err) {
        // silent — user is still typing
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [search, kind]);

  const togglePick = (record) => {
    setPicked((prev) => {
      const next = { ...prev };
      if (next[record.id]) delete next[record.id];
      else next[record.id] = record;
      return next;
    });
  };

  const removePick = (id) => {
    setPicked((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const pickedList = Object.values(picked);

  const labelFor = (r) =>
    kind === 'companies'
      ? r.name
      : `${r.first_name || ''} ${r.last_name || ''}`.trim();

  const subFor = (r) =>
    kind === 'companies'
      ? r.category || ''
      : [r.id_number, r.email].filter(Boolean).join(' · ');

  const startMerge = () => {
    if (pickedList.length < 2) return;
    onPickGroup({
      manual: true,
      similarity: 0,
      records: pickedList,
    });
    setPicked({});
    setSearch('');
    setResults([]);
  };

  return (
    <Card className="border-purple-200">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Hand className="w-5 h-5 text-purple-600" />
          Manual merge
        </CardTitle>
        <CardDescription>
          Search by name and pick {kind === 'companies' ? 'companies' : 'students'} to merge — useful when records have totally different names but are the same {kind === 'companies' ? 'company' : 'person'} (nicknames, ID-name vs everyday name, etc.).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder={kind === 'companies' ? 'Search company name, contact, email...' : 'Search by name, ID number or email...'}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid={`manual-search-${kind}`}
          />
        </div>

        {pickedList.length > 0 && (
          <div className="rounded-md border border-purple-200 bg-purple-50 p-3">
            <div className="text-xs uppercase tracking-wide text-purple-700 font-medium mb-2">
              Picked ({pickedList.length})
            </div>
            <div className="flex flex-wrap gap-2">
              {pickedList.map((r) => (
                <span
                  key={r.id}
                  className="inline-flex items-center gap-1.5 text-xs bg-white border border-purple-300 rounded-full px-2.5 py-1"
                >
                  {labelFor(r)}
                  <button
                    onClick={() => removePick(r.id)}
                    className="text-slate-400 hover:text-red-600"
                    type="button"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            <Button
              size="sm"
              className="mt-3 bg-purple-600 hover:bg-purple-700"
              disabled={pickedList.length < 2}
              onClick={startMerge}
              data-testid={`manual-merge-${kind}-btn`}
            >
              <Merge className="w-4 h-4 mr-2" />
              {pickedList.length < 2
                ? 'Pick at least 2 records'
                : `Merge ${pickedList.length} selected records`}
            </Button>
          </div>
        )}

        {loading && (
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Loader2 className="w-4 h-4 animate-spin" /> Searching...
          </div>
        )}

        {!loading && search.trim().length >= 2 && results.length === 0 && (
          <div className="text-sm text-slate-500 italic">No matches.</div>
        )}

        {results.length > 0 && (
          <div className="border border-slate-200 rounded-md max-h-64 overflow-y-auto divide-y divide-slate-100">
            {results.map((r) => {
              const isPicked = !!picked[r.id];
              return (
                <label
                  key={r.id}
                  className={`flex items-center gap-3 p-2.5 cursor-pointer hover:bg-slate-50 ${isPicked ? 'bg-purple-50' : ''}`}
                >
                  <Checkbox
                    checked={isPicked}
                    onCheckedChange={() => togglePick(r)}
                    data-testid={`manual-pick-${r.id}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">
                      {labelFor(r)}
                    </div>
                    {subFor(r) && (
                      <div className="text-xs text-slate-500 truncate">{subFor(r)}</div>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
        )}

        {search.trim().length < 2 && pickedList.length === 0 && (
          <div className="text-xs text-slate-500">
            Type at least 2 characters to search. Pick 2 or more records, then click <strong>Merge selected</strong>.
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const DuplicatesTab = ({ kind }) => {
  const [groups, setGroups] = useState([]);
  const [manualGroups, setManualGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasScanned, setHasScanned] = useState(false);

  const findDuplicates = async () => {
    setLoading(true);
    try {
      const endpoint =
        kind === 'companies'
          ? '/merge/companies/find-duplicates'
          : '/merge/persons/find-duplicates';
      const res = await axios.get(`${API_URL}${endpoint}`);
      setGroups(res.data.groups || []);
      setHasScanned(true);
      if ((res.data.groups || []).length === 0) {
        toast.success('No duplicates found 🎉');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to scan for duplicates');
    } finally {
      setLoading(false);
    }
  };

  const onManualPicked = (group) => {
    setManualGroups((prev) => [group, ...prev]);
    toast.success('Records added below — pick the winner and confirm.');
  };

  return (
    <div className="space-y-4">
      <ManualSearchSection kind={kind} onPickGroup={onManualPicked} />

      {manualGroups.map((g, idx) => (
        <DuplicateGroupCard
          key={`manual-${kind}-${idx}-${g.records[0].id}`}
          group={g}
          kind={kind}
          onMerged={() => {
            setManualGroups((prev) => prev.filter((_, i) => i !== idx));
          }}
        />
      ))}

      <Card>
        <CardContent className="p-4 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="font-medium text-slate-900">
              Scan for {kind === 'companies' ? 'company' : 'student'} duplicates
            </div>
            <div className="text-sm text-slate-500">
              {kind === 'companies'
                ? 'Compares company names (≥85% similar) and exact matches on email or telephone.'
                : 'Compares names (≥85% similar) and exact matches on ID number or phone (telephone or mobile). Email is ignored on purpose — two people can share a corporate inbox.'}
            </div>
          </div>
          <Button
            onClick={findDuplicates}
            disabled={loading}
            data-testid={`scan-${kind}-btn`}
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Scanning...
              </>
            ) : (
              <>
                <Search className="w-4 h-4 mr-2" /> {hasScanned ? 'Re-scan' : 'Find duplicates'}
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {hasScanned && groups.length === 0 && !loading && (
        <Card>
          <CardContent className="p-8 text-center text-slate-500">
            <ShieldAlert className="w-10 h-10 mx-auto mb-3 text-emerald-500" />
            <p className="font-medium text-slate-900">No duplicates detected.</p>
            <p className="text-sm mt-1">Your {kind === 'companies' ? 'company' : 'student'} list is clean.</p>
          </CardContent>
        </Card>
      )}

      {groups.map((g, idx) => (
        <DuplicateGroupCard
          key={`${kind}-${idx}-${g.records[0].id}`}
          group={g}
          kind={kind}
          onMerged={findDuplicates}
          onIgnored={findDuplicates}
        />
      ))}

      <IgnoredPairsSection kind={kind === 'companies' ? 'companies' : 'persons'} />
    </div>
  );
};

const IgnoredPairsSection = ({ kind }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const fetchIgnored = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/merge/ignored?kind=${kind}`);
      setItems(res.data.items || []);
    } catch (err) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIgnored();
  }, [kind]);

  const removeOne = async (pairId) => {
    try {
      await axios.delete(`${API_URL}/merge/ignored/${pairId}`);
      toast.success('Pair will be suggested again next scan');
      fetchIgnored();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove');
    }
  };

  if (items.length === 0 && !loading) return null;

  return (
    <Card className="border-slate-200">
      <CardHeader
        className="cursor-pointer pb-3"
        onClick={() => setExpanded((v) => !v)}
        data-testid={`ignored-pairs-toggle-${kind}`}
      >
        <CardTitle className="text-sm flex items-center gap-2">
          <EyeOff className="w-4 h-4 text-slate-500" />
          {items.length} ignored pair{items.length === 1 ? '' : 's'} (won't be suggested)
          <span className="text-xs text-slate-500 ml-auto">
            {expanded ? 'Hide' : 'Show'}
          </span>
        </CardTitle>
      </CardHeader>
      {expanded && (
        <CardContent className="pt-0 space-y-2">
          {items.map((p) => (
            <div
              key={p.id}
              className="flex items-center justify-between text-sm py-2 px-3 rounded bg-slate-50 border border-slate-200"
            >
              <div>
                <span className="text-slate-700">
                  {p.names?.join('  ↔  ')}
                </span>
                <span className="text-xs text-slate-400 ml-2">
                  by {p.created_by} · {new Date(p.created_at).toLocaleDateString()}
                </span>
              </div>
              <button
                onClick={() => removeOne(p.id)}
                className="text-slate-400 hover:text-red-600 p-1"
                title="Remove from ignored list (will be suggested again)"
                data-testid={`unignore-${p.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </CardContent>
      )}
    </Card>
  );
};

const MergeDuplicates = () => {
  usePageTitle('Merge Duplicates');
  const navigate = useNavigate();

  return (
    <div className="space-y-6 animate-fade-in" data-testid="merge-page">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/admin-settings')}
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <div>
          <h1
            className="text-3xl font-bold text-slate-900"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Merge Duplicates
          </h1>
          <p className="text-slate-500 mt-1">
            Find and merge duplicate companies and students. Merged records are soft-deleted (recoverable).
          </p>
        </div>
      </div>

      <Tabs defaultValue="companies" className="space-y-4">
        <TabsList>
          <TabsTrigger value="companies" data-testid="tab-companies">
            <Building className="w-4 h-4 mr-2" /> Companies
          </TabsTrigger>
          <TabsTrigger value="students" data-testid="tab-students">
            <Users className="w-4 h-4 mr-2" /> Students
          </TabsTrigger>
        </TabsList>
        <TabsContent value="companies">
          <DuplicatesTab kind="companies" />
        </TabsContent>
        <TabsContent value="students">
          <DuplicatesTab kind="persons" />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MergeDuplicates;
